import { Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { SignalRService } from '../../../main/shared/services/signalr.service';
import { HttpClient } from '@angular/common/http';
import { HubConnectionState } from '@microsoft/signalr';

@Injectable({ providedIn: 'root' })
export class ChatSignalRService implements OnDestroy {
  private hubName = environment.hubs.chatHub.name;
  private hubUrl = environment.hubs.chatHub.url;
  private consumerId = 'chat-consumer';
  private chatSub?: Subscription;

  constructor(private signalR: SignalRService, private http: HttpClient) {}

  /**
   * Observable stream of connection state changes for this hub
   */
  get connectionState$(): Observable<any> {
    return this.signalR.connectionState$.pipe(
      filter((state) => state.hub === this.hubName),
      map((state) => state.state)
    );
  }

  /**
   * Observable stream of new messages
   */
  get newMessage$(): Observable<any> {
    return this.signalR.on<any>(this.hubName, 'ReceiveChatMessage');
  }

  /**
   * Connect to the chat hub with specific group parameters
   * @param groupId The ID of the chat group
   */
  async connect(groupId: string): Promise<void> {
    await this.disconnect();

    const params = new URLSearchParams();
    params.set('groupId', groupId);

    await this.signalR.connect(
      this.hubName,
      this.hubUrl,
      params,
      this.consumerId
    );
  }

  /**
   * Subscribe to incoming chat messages
   * @param cb Callback function to handle received messages
   */
  onMessage(cb: (msg: any) => void) {
    this.chatSub = this.signalR
      .on<any>(this.hubName, 'ReceiveChatMessage')
      .subscribe(cb);
  }

  /**
   * Send a message to the chat group
   * @param message The message data transfer object
   */
  async sendMessage(message: any): Promise<void> {
    await this.signalR.invoke(this.hubName, 'SendMessageToGroup', message);
  }

  /**
   * Send a message to the chat group (alias for sendMessage)
   * @param dto The message data transfer object
   */
  async sendMessageToGroup(dto: any): Promise<void> {
    await this.sendMessage(dto);
  }

  /**
   * Check if currently connected to the chat hub
   */
  isConnected(): boolean {
    const state = this.signalR.getHubState(this.hubName);
    return state === HubConnectionState.Connected;
  }

  /**
   * Disconnect from the chat hub
   */
  async disconnect(): Promise<void> {
    if (this.chatSub) {
      this.chatSub.unsubscribe();
      this.chatSub = undefined;
    }

    await this.signalR.disconnect(this.hubName, true, this.consumerId);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }
}

